import type { ReactNode } from 'react';
import { Button, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { usePopupFocus } from '@shared/lib';
import styles from './FilterPanel.module.css';

interface FilterPanelProps {
  title: string;
  hint?: string;
  children: ReactNode;
  onReset: () => void;
  onApply: () => void;
  // Список со строкой поиска шире, чем пара полей «от — до».
  wide?: boolean;
}

// Каркас выпадашки фильтра в шапке столбца. Выпадашка монтирует панель при
// каждом открытии, поэтому черновик всегда стартует с применённого значения.
// Правки уходят наружу только по «Применить»; «Сбросить» снимает фильтр сразу.
export function FilterPanel({ title, hint, children, onReset, onApply, wide }: FilterPanelProps) {
  const ref = usePopupFocus<HTMLDivElement>();

  return (
    <div
      ref={ref}
      className={`${styles.panel} ${wide ? styles.wide : ''}`}
      role="dialog"
      aria-label={`Фильтр «${title}»`}
      // antd закрывает выпадашку фильтра по Tab, пойманному на window: без
      // этого между полями фильтра не перейти.
      onKeyDown={(e) => {
        if (e.key === 'Tab') e.stopPropagation();
      }}
    >
      <div className={styles.title}>
        {title}
        {hint && (
          <Tooltip title={hint}>
            <QuestionCircleOutlined className={styles.hintIcon} />
          </Tooltip>
        )}
      </div>
      {children}
      <div className={styles.footer}>
        <Button size="small" onClick={onReset}>Сбросить</Button>
        <Button size="small" type="primary" onClick={onApply}>Применить</Button>
      </div>
    </div>
  );
}

/** Подзаголовок секции, когда в одном фильтре их несколько. */
export function FilterSubtitle({ children }: { children: ReactNode }) {
  return <div className={styles.subtitle}>{children}</div>;
}
